import React, { useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useApp } from "@/context/AppContext";
import { ArrowLeft, Lock, CheckCircle2, Play } from "lucide-react";

export default function LevelView() {
  const { levelId } = useParams();
  const navigate = useNavigate();
  const { overlay, refreshCurriculum } = useApp();
  useEffect(() => { refreshCurriculum && refreshCurriculum(); }, [refreshCurriculum]);

  const level = overlay?.levels.find((l) => l.id === levelId);
  if (!overlay) return <div className="p-16 text-center font-cursive text-2xl text-[#0D9488] animate-pulse">Loading…</div>;
  if (!level) return <div className="p-16 text-center">Level not found. <button onClick={() => navigate("/")} className="underline">Home</button></div>;

  return (
    <div className="mx-auto max-w-5xl px-4 py-8 sm:px-6 sm:py-10">
      <button onClick={() => navigate("/writing")} data-testid="level-back" className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#6B7280] hover:text-[#0D9488]"><ArrowLeft size={16} /> Learning Path</button>
      <div className="mt-3">
        <div className="text-xs uppercase tracking-widest font-bold text-[#F59E0B]">Level {level.level_number}</div>
        <h1 className="font-heading text-3xl sm:text-4xl font-extrabold text-[#1F2937]">{level.title}</h1>
        <p className="mt-1 font-body text-[#6B7280]">{level.description}</p>
        {!level.coming_soon && (
          <div className="mt-3 flex items-center gap-3">
            <div className="h-3 w-56 max-w-[60vw] overflow-hidden rounded-full bg-[#F2ECE1]"><div className="h-full rounded-full bg-[#0D9488]" style={{ width: `${level.progress_percentage ?? level.completion_percentage}%` }} /></div>
            <span className="font-bold text-[#1F2937]">{level.progress_percentage ?? level.completion_percentage}% · {level.activities_attempted ?? level.activities_completed}/{level.activities_total} done · {level.activities_mastered ?? level.activities_completed} mastered</span>
          </div>
        )}
      </div>

      {level.coming_soon && (
        <div className="mt-6 rounded-3xl border-2 border-dashed border-[#E5DECF] bg-[#F2ECE1]/40 p-8 text-center">
          <div className="text-4xl">🛠️</div>
          <div className="mt-2 font-heading text-xl font-bold text-[#6B7280]">Content coming soon</div>
          <p className="mt-1 font-body text-[#94A3B8]">This level's activities are being prepared by the curriculum team.</p>
        </div>
      )}

      <div className="mt-8 space-y-8">
        {level.groups.map((g) => (
          <div key={g.id}>
            <h2 className="font-heading text-lg font-bold text-[#1F2937]">{g.title}</h2>
            <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {g.activities.map((a, idx) => {
                const locked = a.status === "locked";
                const mastered = a.status === "mastered";
                return (
                  <motion.button
                    key={a.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: idx * 0.03 }}
                    disabled={locked}
                    onClick={() => !locked && navigate(`/activity/${a.id}`)}
                    data-testid={`activity-${a.id}`}
                    className={`flex items-center justify-between rounded-2xl border p-4 text-left transition-all ${locked ? "border-[#E5DECF] bg-[#F2ECE1]/50 cursor-not-allowed" : "border-[#E5DECF] bg-white hover:shadow-md hover:-translate-y-0.5"}`}>
                    <div className="min-w-0">
                      <div className="font-heading font-bold text-[#1F2937] truncate">{a.title}</div>
                      {a.attempts > 0 && <div className="text-xs text-[#6B7280]">Best {a.best_score}%</div>}
                    </div>
                    {mastered ? <CheckCircle2 size={22} color="#10B981" /> : locked ? <Lock size={20} color="#94A3B8" /> : <Play size={20} color="#0D9488" />}
                  </motion.button>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
